import React from 'react';
import { Box, Typography, Row, Col, Image } from '@lucid/lego';
import type { Theme, VehicleData } from '../types';
import styles from './VehicleCard.module.scss';

type VehicleCardProps = {
  /** Vehicle asset data */
  asset: VehicleData;
  /** Theme for the component */
  theme: Theme;
  /** Callback when card is clicked */
  onClick: () => void;
  /** Test ID for testing */
  dataTestId: string;
};

/**
 * Vehicle card component displaying vehicle info in search results
 * @component
 * @param {VehicleCardProps} props - Component props
 * @returns {JSX.Element} Clickable vehicle card with image and details
 * @example
 * <VehicleCard
 *   asset={vehicleData}
 *   theme="dark"
 *   onClick={() => handleSelect(vehicleData)}
 *   dataTestId="vehicle-card-0"
 * />
 */
const VehicleCard: React.FC<VehicleCardProps> = ({ asset, theme, onClick, dataTestId }) => {
  return (
    <Box
      background="white"
      pb={32}
      pl={24}
      pr={24}
      pt={32}
      className={styles.cardContainer}
      data-testid={dataTestId}
      onClick={onClick}
      theme={theme}
    >
      {/* Vehicle Title */}
      <Box mb={16}>
        <Typography variant="h3" size="medium" color="black" theme={theme}>
          {asset.vehicle.year} {asset.vehicle.model}
        </Typography>
      </Box>

      {/* Vehicle Content */}
      <Row gutter={[24, 24]} className={styles.stretchRow}>
        {/* Vehicle Image */}
        <Col xs={12} sm={12} md={5} lg={5}>
          <Box className={styles.imageBox}>
            <Image
              uri_mobile={asset.vehicle.image.uri_mobile}
              uri_tablet={asset.vehicle.image.uri_tablet}
              uri_desktop={asset.vehicle.image.uri_desktop}
              alt={asset.vehicle.image.alt}
              theme={theme}
              rounded={true}
              useAspectRatio={false}
            />
          </Box>
        </Col>

        {/* Vehicle Details */}
        <Col xs={12} sm={12} md={7} lg={7}>
          <Box pl={24} pr={24} pt={24} pb={24} className={styles.innerCard}>
            <Row gutter={[16, 16]}>
              {asset.contacts.map((contact, contactIndex) => (
                <Col xs={12} sm={6} md={6} lg={6} key={contactIndex}>
                  <Box>
                    <Typography variant="body" size="small" color="gray-5" theme={theme}>
                      {contact.label}
                    </Typography>
                    <Typography variant="body" size="medium" color="black">
                      {contact.value}
                    </Typography>
                  </Box>
                </Col>
              ))}
            </Row>
          </Box>
        </Col>
      </Row>
    </Box>
  );
};

export default VehicleCard;
